// Cardio (.fit) blocks shared by the session inspector and the TRENDS page:
// RunResult / PadelResult (zone-discipline readouts + HR trace + zone bars),
// FitDrop (upload target), SessionList, RunningCapacity, PadelConsistency.
// Design reference: design handoff `prototype/ledger-cardio.jsx`.

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactElement } from 'react';
import { apiClient, formatApiError } from '../../lib/api';
import {
  computePadelStreak,
  mondayOfToday,
  recentPadelWeeks,
  shiftMondayWeeks,
  thisWeekHasPadel,
  useCardioSessionDetail,
  useCardioSessions,
  type CardioKind,
} from '../../lib/cardio-queries';
import {
  countZ2Runs,
  longestRun,
  paceAtZ2Trend,
  weeklyVolume,
  weeklyZ2Time,
} from '../../lib/cardio-weeks';
import { useProfile } from '../../lib/day-data';
import { C } from '../../lib/design';
import { dayLabel, fmtClock, fmtDur, fmtMinutes, fmtPace, sessionLabel } from '../../lib/ledger';
import { useLedger } from '../../lib/ledger-state';
import { hrZones } from '../../lib/protocol';
import {
  DEFAULT_PROFILE,
  formatRunSegments,
  WorkoutRowsSchema,
  type CardioSessionSummary,
  type HrZonesSnapshot,
  type MinPerZone,
  type PrescribedRun,
  type WorkoutRow,
} from '../../lib/types';
import { STALE_TIME_DAILY } from '../../lib/query';
import { useFitUpload, type FitUploadResult } from './hooks';
import { Bar, Btn, DISPLAY, ErrorStrip, Lbl, Panel, Sub } from './ui';

// ── Zones ──────────────────────────────────────────────────────────────────

export type ZoneKey = 'Z1' | 'Z2' | 'Z3' | 'Z4' | 'Z5';
const ZONE_KEYS: readonly ZoneKey[] = ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'];
/** Lo-inclusive / hi-exclusive bpm band per zone. */
export type ZoneBands = Record<ZoneKey, [number, number]>;

// Zone ramp for an unprescribed trace (padel): quiet → bright → hot, on-palette.
const ZONE_RAMP: Record<ZoneKey, string> = {
  Z1: C.faint,
  Z2: C.dim,
  Z3: C.signal,
  Z4: C.warn,
  Z5: C.red,
};

/** The session's own zone snapshot when the parser stored one, else the
 *  profile-age Tanaka zones (normalised to hi-exclusive so membership math is
 *  identical to the backend's). */
export function zoneBands(snapshot: HrZonesSnapshot | null, age: number | null): ZoneBands {
  if (snapshot) {
    return { Z1: snapshot.z1, Z2: snapshot.z2, Z3: snapshot.z3, Z4: snapshot.z4, Z5: snapshot.z5 };
  }
  const zones = hrZones(age ?? DEFAULT_PROFILE.age ?? 45);
  const bands = {} as ZoneBands;
  for (const z of zones) bands[z.name] = [z.range[0], z.name === 'Z5' ? Infinity : z.range[1] + 1];
  return bands;
}

function useZoneBands(session: CardioSessionSummary): ZoneBands {
  const age = useProfile().data?.age ?? null;
  return zoneBands(session.hr_zones_snapshot, age);
}

function zoneOf(bpm: number, bands: ZoneBands): ZoneKey {
  for (const z of ZONE_KEYS) {
    const [lo, hi] = bands[z];
    if (bpm >= lo && bpm < hi) return z;
  }
  return 'Z5';
}

/** Generic workout_type → zone, so the band renders before the detail (and
 *  its coach prescription) arrives, and for pre-0028 sessions. */
function zoneForType(type: string | null): ZoneKey | null {
  switch (type) {
    case 'Run-Easy':
    case 'Run-Long':
      return 'Z2';
    case 'Run-Tempo':
      return 'Z3';
    case 'Run-Intervals':
      return 'Z4';
    case 'Run-Recovery':
      return 'Z1';
    default:
      return null;
  }
}

function minutesPerZone(s: MinPerZone | null): Record<ZoneKey, number> | null {
  if (!s) return null;
  return { Z1: s.z1 / 60, Z2: s.z2 / 60, Z3: s.z3 / 60, Z4: s.z4 / 60, Z5: s.z5 / 60 };
}

// ── Primitives ─────────────────────────────────────────────────────────────

export function Readout({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string | null;
  color?: string;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <Lbl>{label}</Lbl>
      <div className="num" style={{ fontSize: 15, marginTop: 4, color: color ?? C.text, whiteSpace: 'nowrap' }}>
        {value}
      </div>
      {sub && (
        <Sub className="num" style={{ marginTop: 2 }}>
          {sub}
        </Sub>
      )}
    </div>
  );
}

const TRACE_W = 400;
const TRACE_POINTS = 400;
const TRACE_LO = 90;
const TRACE_HI = 185;

/** A .fit HR series is one sample per second; bucket-average it down to one
 *  point per viewBox pixel so the SVG stays at ≤400 segments. */
function downsample(hr: number[]): number[] {
  if (hr.length <= TRACE_POINTS) return hr;
  const out: number[] = [];
  const step = hr.length / TRACE_POINTS;
  for (let i = 0; i < TRACE_POINTS; i++) {
    const from = Math.floor(i * step);
    const to = Math.max(from + 1, Math.floor((i + 1) * step));
    let sum = 0;
    for (let j = from; j < to; j++) sum += hr[j]!;
    out.push(sum / (to - from));
  }
  return out;
}

/** HR trace, 400×96. With a prescribed `zone` the band is a 7% signal rect
 *  with dashed bounds and each segment is faint (below) / signal (in) /
 *  warn (above) / red (Z5); without one the zone ramp colours the line. */
export function ZoneHr({
  hr,
  zones,
  zone,
  h,
}: {
  hr: number[];
  zones: ZoneBands;
  zone?: ZoneKey | null;
  h?: number;
}) {
  const H = h ?? 96;
  const pad = 4;
  const pts = downsample(hr);
  if (pts.length < 2) {
    return (
      <div style={{ height: H, border: `1px dashed ${C.line}`, display: 'grid', placeItems: 'center' }}>
        <Lbl>NO HR SERIES</Lbl>
      </div>
    );
  }
  const y = (v: number) =>
    H - pad - ((Math.max(TRACE_LO, Math.min(TRACE_HI, v)) - TRACE_LO) / (TRACE_HI - TRACE_LO)) * (H - pad * 2);
  const xs = (TRACE_W - pad * 2) / (pts.length - 1);
  const band = zone ? zones[zone] : null;
  const col = (v: number) => {
    const z = zoneOf(v, zones);
    if (!band) return ZONE_RAMP[z];
    if (v < band[0]) return C.faint;
    if (v < band[1]) return C.signal;
    return z === 'Z5' ? C.red : C.warn;
  };
  const segs: ReactElement[] = [];
  for (let i = 1; i < pts.length; i++) {
    segs.push(
      <line
        key={i}
        x1={pad + (i - 1) * xs}
        y1={y(pts[i - 1]!)}
        x2={pad + i * xs}
        y2={y(pts[i]!)}
        stroke={col(pts[i]!)}
        strokeWidth="1.5"
      />,
    );
  }
  return (
    <svg
      viewBox={`0 0 ${TRACE_W} ${H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {band && (
        <rect
          x={pad}
          width={TRACE_W - pad * 2}
          y={y(band[1])}
          height={y(band[0]) - y(band[1])}
          fill={C.signal}
          fillOpacity="0.07"
        />
      )}
      {band &&
        band.map((b, i) => (
          <line key={i} x1={pad} x2={TRACE_W - pad} y1={y(b)} y2={y(b)} stroke={C.line} strokeDasharray="3 3" />
        ))}
      {segs}
    </svg>
  );
}

/** Five Z1–Z5 columns: minutes + 4px bar, prescribed zone highlighted. */
export function ZoneBars({
  minPerZone,
  zone,
}: {
  minPerZone: Record<ZoneKey, number>;
  zone?: ZoneKey | null;
}) {
  const total = ZONE_KEYS.reduce((a, z) => a + minPerZone[z], 0) || 1;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 6 }}>
      {ZONE_KEYS.map((z) => {
        const m = minPerZone[z];
        const on = z === zone;
        return (
          <div key={z}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Lbl color={on ? C.signal : undefined}>{z}</Lbl>
              <Sub className="num" color={on ? C.text : C.faint}>
                {Math.round(m)}′
              </Sub>
            </div>
            <div style={{ marginTop: 4 }}>
              <Bar value={m} max={total} color={on ? C.signal : C.faint} height={4} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TracePlaceholder({ label }: { label: string }) {
  return (
    <div style={{ height: 96, border: `1px dashed ${C.line}`, display: 'grid', placeItems: 'center' }}>
      <Lbl>{label}</Lbl>
    </div>
  );
}

// ── Results ────────────────────────────────────────────────────────────────

function planLabel(p: PrescribedRun): string {
  if (p.segments && p.segments.length > 0) return formatRunSegments(p.segments);
  if (p.duration_min != null) return `${p.duration_min} min @ ${p.zone}`;
  return p.description ?? p.zone;
}

/** Ratio of minutes in the prescribed zone to the prescribed minutes there;
 *  on target within 0.8–1.25. Prefers the prescription's own per-zone minutes
 *  (a segmented run prescribes less time in its dominant zone than its total
 *  duration), falling back to duration_min. */
function verdictFor(
  prescribed: PrescribedRun | null,
  minPerZone: Record<ZoneKey, number> | null,
  pctInZone: number | null,
): [string, string] | null {
  if (prescribed) {
    if (!minPerZone) return null;
    const target = prescribed.min_per_zone[prescribed.zone.toLowerCase() as keyof MinPerZone] || prescribed.duration_min;
    if (!target || target <= 0) return null;
    const r = minPerZone[prescribed.zone] / target;
    if (r >= 0.8 && r <= 1.25) return [`✓ ON TARGET · ${prescribed.zone}`, C.signal];
    if (r < 0.8) return [`↓ UNDER · ${prescribed.zone}`, C.warn];
    return [`↑ OVER · ${prescribed.zone}`, C.warn];
  }
  if (pctInZone == null) return null;
  const pct = Math.round(pctInZone);
  return [`${pct}% IN ZONE`, pct >= 80 ? C.signal : C.warn];
}

export function RunResult({ session }: { session: CardioSessionSummary }) {
  const detailQ = useCardioSessionDetail(session.id);
  const zones = useZoneBands(session);
  const detail = detailQ.data ?? null;
  const prescribed = detail?.prescribed_run ?? null;
  const zone = prescribed?.zone ?? zoneForType(session.workout_type);
  const minPerZone = minutesPerZone(detail?.time_per_zone_s ?? null);
  const verdict = verdictFor(prescribed, minPerZone, session.pct_in_prescribed_zone);

  const dur = session.duration_s ?? 0;
  const km = session.distance_m != null ? session.distance_m / 1000 : null;
  const pace = km && km > 0 && dur > 0 ? fmtPace(dur / km) : '—';
  const avg = session.avg_hr_bpm != null ? Math.round(session.avg_hr_bpm) : null;
  const max = session.max_hr_bpm != null ? Math.round(session.max_hr_bpm) : null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <Lbl style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          PLAN · {prescribed ? planLabel(prescribed) : detailQ.isLoading ? '…' : '—'}
        </Lbl>
        {verdict && (
          <Lbl color={verdict[1]} style={{ whiteSpace: 'nowrap' }}>
            {verdict[0]}
          </Lbl>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginTop: 12 }}>
        <Readout
          label="TIME"
          value={fmtDur(dur)}
          sub={prescribed?.duration_min != null ? `of ${prescribed.duration_min}:00` : null}
        />
        <Readout label="DIST" value={km != null ? `${km.toFixed(2)} km` : '—'} />
        <Readout label="PACE" value={pace} sub="BY-PRODUCT" />
        <Readout label="HR" value={avg != null ? `${avg} avg` : '—'} sub={max != null ? `${max} max` : null} />
      </div>
      <div style={{ marginTop: 14 }}>
        {detailQ.isLoading ? (
          <TracePlaceholder label="LOADING…" />
        ) : detail?.hr_series ? (
          <ZoneHr hr={detail.hr_series.hr_bpm} zones={zones} zone={zone} />
        ) : (
          <TracePlaceholder label="NO HR SERIES" />
        )}
      </div>
      {minPerZone && (
        <div style={{ marginTop: 12 }}>
          <ZoneBars minPerZone={minPerZone} zone={zone} />
        </div>
      )}
      {detailQ.isError && (
        <div style={{ marginTop: 10 }}>
          <ErrorStrip message={formatApiError(detailQ.error)} />
        </div>
      )}
    </div>
  );
}

export function PadelResult({ session }: { session: CardioSessionSummary }) {
  const detailQ = useCardioSessionDetail(session.id);
  const zones = useZoneBands(session);
  const detail = detailQ.data ?? null;
  const minPerZone = minutesPerZone(detail?.time_per_zone_s ?? null);
  const avg = session.avg_hr_bpm != null ? Math.round(session.avg_hr_bpm) : null;
  const max = session.max_hr_bpm != null ? Math.round(session.max_hr_bpm) : null;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        <Readout label="TIME" value={fmtDur(session.duration_s ?? 0)} />
        <Readout
          label="ABOVE Z3"
          value={session.time_above_z3_s != null ? `${fmtMinutes(session.time_above_z3_s)}′` : '—'}
          sub="TIME IN Z3+"
        />
        <Readout label="AVG HR" value={avg != null ? String(avg) : '—'} />
        <Readout
          label="MAX HR"
          value={max != null ? String(max) : '—'}
          color={max != null && max >= zones.Z5[0] ? C.red : C.text}
        />
      </div>
      <div style={{ marginTop: 14 }}>
        {detailQ.isLoading ? (
          <TracePlaceholder label="LOADING…" />
        ) : detail?.hr_series ? (
          <ZoneHr hr={detail.hr_series.hr_bpm} zones={zones} />
        ) : (
          <TracePlaceholder label="NO HR SERIES" />
        )}
      </div>
      {minPerZone && (
        <div style={{ marginTop: 12 }}>
          <ZoneBars minPerZone={minPerZone} />
        </div>
      )}
      {detailQ.isError && (
        <div style={{ marginTop: 10 }}>
          <ErrorStrip message={formatApiError(detailQ.error)} />
        </div>
      )}
    </div>
  );
}

// ── Upload ─────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<FitUploadResult['status'], [string, string]> = {
  parsed: ['PARSED', C.signal],
  duplicate: ['DUPLICATE', C.dim],
  failed: ['FAILED', C.red],
};

/** Per-file outcome rows after an upload. */
function FitResults({ results }: { results: FitUploadResult[] }) {
  if (results.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left' }}>
      {results.map((r, i) => {
        const [label, color] = STATUS_LABEL[r.status];
        const res = r.response;
        return (
          <div key={`${r.filename}-${i}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11 }}>
              <span style={{ color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.filename}
              </span>
              <Lbl color={color} style={{ whiteSpace: 'nowrap' }}>
                {label}
              </Lbl>
            </div>
            {r.error && <Sub color={C.red}>{r.error}</Sub>}
            {res && r.status !== 'failed' && !res.workout_log_match && (
              <Sub color={C.warn}>
                NO SESSION MATCH · recorded {new Date(res.recorded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).toUpperCase()}
              </Sub>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Dashed upload target. Click the primary or drop Apple Watch exports on it. */
export function FitDrop({ kind, compact }: { kind: CardioKind; compact?: boolean }) {
  const { open, upload, busy, results, inputProps } = useFitUpload(kind);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!over) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!busy) void upload(Array.from(e.dataTransfer.files).filter((f) => f.name.toLowerCase().endsWith('.fit')));
      }}
      style={{
        border: `1px dashed ${over ? C.signal : C.line}`,
        padding: compact ? '10px 12px' : '18px 14px',
        textAlign: 'center',
      }}
    >
      <input {...inputProps} />
      <Lbl color={C.dim}>{compact ? 'REPLACE .FIT' : 'NO .FIT FOR THIS SESSION'}</Lbl>
      {!compact && <Sub style={{ marginTop: 6 }}>Drop the Apple Watch export here, or</Sub>}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: compact ? 8 : 10 }}>
        <Btn primary={!compact} onClick={open} disabled={busy}>
          {busy ? 'UPLOADING…' : '↑ UPLOAD .FIT'}
        </Btn>
      </div>
      {results.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <FitResults results={results} />
        </div>
      )}
    </div>
  );
}

// ── Session list ───────────────────────────────────────────────────────────

const LIST_LIMIT = 8;
const LIST_LIMIT_ALL = 50;

/** Newest-first sessions of one kind; each row expands to its result block. */
export function SessionList({ kind }: { kind: CardioKind }) {
  const [limit, setLimit] = useState(LIST_LIMIT);
  const q = useCardioSessions(kind, { limit });
  const rows = [...(q.data ?? [])].sort((a, b) => b.recorded_at - a.recorded_at);
  // `undefined` = nothing toggled yet → the newest row opens by default.
  const [openId, setOpenId] = useState<number | null | undefined>(undefined);
  const open = openId === undefined ? (rows[0]?.id ?? null) : openId;
  const { open: pick, busy, results, inputProps } = useFitUpload(kind);
  const ledger = useLedger();
  const navigate = useNavigate();

  return (
    <Panel
      title={`${kind.toUpperCase()} · SESSIONS`}
      right={
        <Btn onClick={pick} disabled={busy} style={{ color: C.signal, padding: '4px 10px' }}>
          {busy ? 'UPLOADING…' : '↑ UPLOAD .FIT'}
        </Btn>
      }
    >
      <input {...inputProps} />
      {results.length > 0 && (
        <div style={{ padding: '10px 16px 0' }}>
          <FitResults results={results} />
        </div>
      )}
      {q.isLoading ? (
        <div style={{ padding: 16 }}>
          <Lbl>LOADING…</Lbl>
        </div>
      ) : q.isError ? (
        <div style={{ padding: 16 }}>
          <ErrorStrip message={formatApiError(q.error)} />
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 16, fontSize: 12, color: C.dim }}>No sessions yet.</div>
      ) : (
        rows.map((s, i) => {
          const isOpen = open === s.id;
          const date = s.workout_log_match?.date ?? s.recorded_date;
          const km = s.distance_m != null ? (s.distance_m / 1000).toFixed(1) : null;
          return (
            <div key={s.id} style={{ borderTop: i === 0 && results.length === 0 ? 0 : `1px solid ${C.line}` }}>
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : s.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 12,
                  background: 'transparent',
                  border: 0,
                  padding: '10px 16px',
                  fontFamily: 'inherit',
                  color: C.text,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 12 }}>
                  {dayLabel(date)}
                  {s.workout_type ? ` · ${sessionLabel(s.workout_type)}` : ''}
                </span>
                <Sub className="num" style={{ whiteSpace: 'nowrap' }}>
                  {fmtDur(s.duration_s ?? 0)}
                  {km ? ` · ${km} km` : ''}
                  {s.avg_hr_bpm != null ? ` · ${Math.round(s.avg_hr_bpm)} avg` : ''}{' '}
                  <span style={{ color: C.signal }}>{isOpen ? '−' : '+'}</span>
                </Sub>
              </button>
              {isOpen && (
                <div style={{ padding: '0 16px 16px' }}>
                  {kind === 'run' ? <RunResult session={s} /> : <PadelResult session={s} />}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 10 }}>
                    <Sub color={C.faint} className="num">
                      RECORDED {fmtClock(s.recorded_at)}
                      {s.workout_log_match ? '' : ' · NO SESSION MATCH'}
                    </Sub>
                    <Btn
                      ghost
                      onClick={() => {
                        ledger.select(date, 'session');
                        navigate({ to: '/log' });
                      }}
                    >
                      OPEN IN LEDGER →
                    </Btn>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
      {rows.length >= limit && limit < LIST_LIMIT_ALL && (
        <div style={{ borderTop: `1px solid ${C.line}`, padding: '8px 16px' }}>
          <Btn ghost onClick={() => setLimit(LIST_LIMIT_ALL)}>
            SHOW ALL
          </Btn>
        </div>
      )}
    </Panel>
  );
}

// ── Trends panels ──────────────────────────────────────────────────────────

const CAP_WEEKS = 8;

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div style={{ border: `1px solid ${C.line}`, background: C.panel2, padding: '10px 12px', minWidth: 0 }}>
      <Lbl>{label}</Lbl>
      <div className="num" style={{ fontSize: 15, marginTop: 6 }}>
        {value}
      </div>
      <Sub className="num" style={{ marginTop: 2 }}>
        {sub}
      </Sub>
    </div>
  );
}

/** Pace-at-Z2 over eight weeks + this week's volume / longest / Z2 time. */
export function RunningCapacity() {
  const q = useCardioSessions('run', { limit: 200 });
  const sessions = q.data ?? [];
  const profile = useProfile().data;
  const thisMon = mondayOfToday();
  const lastMon = shiftMondayWeeks(thisMon, -1);
  const trend = paceAtZ2Trend(sessions, CAP_WEEKS);
  const enough = countZ2Runs(sessions, shiftMondayWeeks(thisMon, -(CAP_WEEKS - 1))) >= 3;
  const points = trend
    .map((p, i) => ({ i, v: p.paceSPerKm }))
    .filter((p): p is { i: number; v: number } => p.v != null);
  const cur = points[points.length - 1]?.v ?? null;
  const first = points[0]?.v ?? null;
  const goal =
    profile?.target_run_time_seconds && profile.target_run_distance_m
      ? fmtPace(profile.target_run_time_seconds / (profile.target_run_distance_m / 1000))
      : '5:41/km';

  const W = 600;
  const H = 110;
  const pad = 6;
  const vals = points.map((p) => p.v);
  const lo = vals.length ? Math.min(...vals) - 6 : 0;
  const hi = vals.length ? Math.max(...vals) + 6 : 1;
  const xs = (W - pad * 2) / (CAP_WEEKS - 1);
  // Faster (smaller) pace draws higher, so the line reads like a capacity curve.
  const y = (v: number) => pad + ((v - lo) / (hi - lo || 1)) * (H - pad * 2);
  const path = points.map((p, k) => `${k ? 'L' : 'M'}${pad + p.i * xs} ${y(p.v)}`).join(' ');

  const vol = weeklyVolume(sessions, thisMon);
  const volPrev = weeklyVolume(sessions, lastMon);
  const longest = longestRun(sessions, thisMon);
  const longestPrev = longestRun(sessions, lastMon);
  const z2 = weeklyZ2Time(sessions, thisMon);
  const z2Prev = weeklyZ2Time(sessions, lastMon);

  return (
    <Panel title="RUNNING · CAPACITY" right="PACE AT Z2 · 8 WEEKS">
      <div style={{ padding: 16 }}>
        {q.isLoading ? (
          <div style={{ height: 150, display: 'grid', placeItems: 'center' }}>
            <Lbl>LOADING…</Lbl>
          </div>
        ) : q.isError ? (
          <ErrorStrip message={formatApiError(q.error)} />
        ) : !enough || cur == null || first == null ? (
          <div style={{ height: 150, border: `1px dashed ${C.line}`, display: 'grid', placeItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <Lbl color={C.dim}>INSUFFICIENT DATA · NEED ≥3 Z2 RUNS</Lbl>
              <Sub style={{ marginTop: 6 }}>Log easy or long runs with HR to see the trend.</Sub>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span className="num" style={{ fontFamily: DISPLAY, fontSize: 30, color: C.signal, lineHeight: 1 }}>
                {fmtPace(cur)}
              </span>
              <Sub className="num">
                at Z2 · {fmtPace(first)} eight weeks ago · goal {goal}
              </Sub>
            </div>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              style={{ width: '100%', height: 'auto', display: 'block', marginTop: 10 }}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path d={path} stroke={C.signal} strokeWidth="1.5" fill="none" />
              {points.map((p, k) => (
                <rect
                  key={p.i}
                  x={pad + p.i * xs - 2}
                  y={y(p.v) - 2}
                  width="4"
                  height="4"
                  fill={k === points.length - 1 ? C.text : C.signal}
                />
              ))}
            </svg>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              {trend.map((p) => (
                <Sub key={p.weekMonday} color={C.faint} className="num">
                  {`${p.weekMonday.slice(8, 10)}/${p.weekMonday.slice(5, 7)}`}
                </Sub>
              ))}
            </div>
          </>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginTop: 14 }}>
          <Tile label="THIS WEEK · VOLUME" value={`${vol.km.toFixed(1)} km`} sub={`${volPrev.km.toFixed(1)} km last week`} />
          <Tile
            label="LONGEST RUN"
            value={longest ? `${longest.km.toFixed(1)} km` : '—'}
            sub={longestPrev ? `${longestPrev.km.toFixed(1)} km last week` : 'none last week'}
          />
          <Tile label="Z2 TIME" value={`${Math.round(z2)}′`} sub={`${Math.round(z2Prev)}′ last week`} />
        </div>
      </div>
    </Panel>
  );
}

const PADEL_WEEKS = 8;

/** Weekly padel streak from completed Padel rows (manual or coach-written —
 *  the server writes a concrete 'Padel' type for coach padel too). */
export function PadelConsistency() {
  const q = useQuery<WorkoutRow[]>({
    queryKey: ['workouts'],
    queryFn: () => apiClient.get<WorkoutRow[]>('/api/workouts', { schema: WorkoutRowsSchema }),
    staleTime: STALE_TIME_DAILY,
  });
  const dates = (q.data ?? []).filter((r) => r.status === 'completed' && r.type === 'Padel').map((r) => r.date);
  const streak = computePadelStreak(dates);
  const played = thisWeekHasPadel(dates);
  const weeks = recentPadelWeeks(dates, PADEL_WEEKS);
  return (
    <Panel title="PADEL · CONSISTENCY" right="8 WEEKS">
      <div style={{ padding: 16 }}>
        {q.isError && (
          <div style={{ marginBottom: 10 }}>
            <ErrorStrip message={formatApiError(q.error)} />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span className="num" style={{ fontFamily: DISPLAY, fontSize: 30, color: C.signal, lineHeight: 1 }}>
            {q.isLoading ? '—' : streak}
          </span>
          <Sub>weeks in a row</Sub>
          <Lbl color={played ? C.signal : C.faint} style={{ marginLeft: 'auto' }}>
            {played ? 'PLAYED THIS WEEK ✓' : 'NOT YET THIS WEEK'}
          </Lbl>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
          {weeks.map((p, i) => (
            <div
              key={i}
              title={i === weeks.length - 1 ? 'This week' : undefined}
              style={{
                width: 14,
                height: 14,
                background: p ? C.signal : 'transparent',
                border: `1px solid ${p ? C.signal : C.faint}`,
              }}
            />
          ))}
        </div>
      </div>
    </Panel>
  );
}
